import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { setPageMeta, resetPageMeta } from '../lib/seo';
import { ArrowLeft, MapPin } from 'lucide-react';
import MapInstantStart from '../components/MapInstantStart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MapPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [showCls, setShowCls] = useState(false);
  const [clsPins, setClsPins] = useState([]);
  const [clsLoaded, setClsLoaded] = useState(false);

  const toggleClassifieds = async () => {
    const next = !showCls;
    setShowCls(next);
    if (next && !clsLoaded) {
      try {
        const { data } = await axios.get(`${API}/classifieds/geo`, { params: { limit: 500 } });
        setClsPins((data || []).map((c) => ({
          lat: c.lat, lng: c.lng, emoji: c.emoji,
          name: c.title, link: `/sludinajums/${c.slug || c.id}`,
        })));
      } catch (e) { /* ignore */ }
      setClsLoaded(true);
    }
  };

  useEffect(() => {
    const origin = window.location.origin;
    setPageMeta({
      title: language === 'lv' ? 'KoLV — Latvijas karte' : language === 'ru' ? 'KoLV — Карта Латвии' : 'KoLV — Latvia Map',
      description: language === 'lv'
        ? 'Interaktīva Latvijas vektoru karte ar visām pilsētām, pagastiem, ciemiem, mikrorajoniem un uzņēmumiem KoLV tirgus vietā.'
        : language === 'ru'
        ? 'Интерактивная векторная карта Латвии со всеми городами, волостями, сёлами, районами и компаниями на маркетплейсе KoLV.'
        : 'Interactive vector map of Latvia with every city, parish, village, district and company on the KoLV marketplace.',
      url: `${origin}/karte`,
    });
    return () => resetPageMeta();
  }, [language]);

  useEffect(() => {
    axios.get(`${API}/locations/stats/summary`)
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="map-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E4E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#121212]"
            data-testid="map-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-outfit text-xl sm:text-2xl font-bold text-[#121212] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#D97736]" />
              {language === 'lv' ? 'Latvijas karte' : language === 'ru' ? 'Карта Латвии' : 'Latvia Map'}
            </h1>
            {stats && (
              <p className="text-xs text-[#5C5C5C]" data-testid="map-stats">
                {stats.by_type?.city || 0} {language === 'lv' ? 'pilsētas' : language === 'ru' ? 'городов' : 'cities'} ·{' '}
                {stats.by_type?.district || 0} {language === 'lv' ? 'mikrorajoni' : language === 'ru' ? 'районов' : 'districts'} ·{' '}
                {stats.by_type?.village || 0} {language === 'lv' ? 'ciemi un pagasti' : language === 'ru' ? 'сёл и волостей' : 'villages & parishes'}
              </p>
            )}
          </div>
          <button
            onClick={toggleClassifieds}
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${showCls ? 'bg-[#2F7E79] text-white border-[#2F7E79]' : 'bg-white text-[#2F7E79] border-[#2F7E79]/40 hover:border-[#2F7E79]'}`}
            data-testid="map-classifieds-toggle"
          >
            <span className="text-base">🧳</span>
            {language === 'lv' ? 'Sludinājumi' : language === 'ru' ? 'Объявления' : 'Classifieds'}
            {showCls && clsPins.length > 0 && <span className="ml-0.5 text-xs bg-white/25 rounded-full px-1.5">{clsPins.length}</span>}
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 p-3 sm:p-4">
        <MapInstantStart height="calc(100vh - 140px)" rememberViewKey="kolv-map-karte" data-testid="full-map"
          extraMarkers={showCls ? clsPins : null} />
      </div>
    </div>
  );
}
